import { jsonKeysWithDifferentValues } from '@revelio/core';

import {
  SELF_MANAGED_PREFERENCE_KEYS,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from '../types';
import { normalizePreferencesForDiff } from './preferences-normalize';

/**
 * Calculate differences for taxonomy sub-keys (role and skill)
 */
function calculateTaxonomyDiffs(
  currentTaxonomy: UserPreferences[UserPreferenceKey.Taxonomies] | undefined,
  savedTaxonomy: UserPreferences[UserPreferenceKey.Taxonomies] | undefined
): UserPreferenceSubKey[] {
  const taxonomyDiffs: UserPreferenceSubKey[] = [];

  if (!currentTaxonomy || !savedTaxonomy) {
    return taxonomyDiffs;
  }

  // Compare role taxonomy specifically
  const currentRole = currentTaxonomy.role;
  const savedRole = savedTaxonomy.role;

  if (currentRole !== savedRole) {
    // Handle different types of role values
    if (typeof currentRole === 'object' && typeof savedRole === 'object') {
      if (JSON.stringify(currentRole) !== JSON.stringify(savedRole)) {
        taxonomyDiffs.push(UserPreferenceSubKey.RoleTaxonomy);
      }
    } else {
      taxonomyDiffs.push(UserPreferenceSubKey.RoleTaxonomy);
    }
  }

  // Compare skill taxonomy specifically
  const currentSkill = currentTaxonomy.skill;
  const savedSkill = savedTaxonomy.skill;

  if (currentSkill !== savedSkill) {
    taxonomyDiffs.push(UserPreferenceSubKey.SkillTaxonomy);
  }

  if (currentTaxonomy.activity !== savedTaxonomy.activity) {
    taxonomyDiffs.push(UserPreferenceSubKey.ActivityTaxonomy);
  }

  return taxonomyDiffs;
}

/**
 * Calculate the differences between current and saved preferences,
 * filtered by visibility function if provided.
 */
export function calculateVisiblePreferenceDiff(
  currentPreferences: UserPreferences,
  savedPreferences: UserPreferences,
  visibleKeys: UserPreferenceKey[],
  hasError = false
): (UserPreferenceKey | UserPreferenceSubKey)[] {
  if (hasError) return [];

  const normalizedCurrent = normalizePreferencesForDiff(currentPreferences);
  const normalizedSaved = normalizePreferencesForDiff(savedPreferences);

  const allDiffs = jsonKeysWithDifferentValues(
    normalizedCurrent,
    normalizedSaved
  );

  const currentTaxonomy = normalizedCurrent[UserPreferenceKey.Taxonomies];
  const savedTaxonomy = normalizedSaved[UserPreferenceKey.Taxonomies];

  const taxonomyDiffs = calculateTaxonomyDiffs(currentTaxonomy, savedTaxonomy);

  const filteredDiffs = allDiffs.filter(
    (key) =>
      visibleKeys.includes(key as UserPreferenceKey) &&
      !SELF_MANAGED_PREFERENCE_KEYS.includes(key as UserPreferenceKey)
  ) as UserPreferenceKey[];

  return [...filteredDiffs, ...taxonomyDiffs];
}
